"use client"

import { useEffect } from "react"
import { getDeviceId, getInfo } from "lib/Cookies"
import { getDeviceBrand, getSessionSign } from "utils/helper"

// Helper để lấy tracking object an toàn (tránh SSR error)
const getTracking = () => (typeof window !== "undefined" ? window.TrackingLib : null)

// Page map configuration
const PAGE_MAP = {
  // Định nghĩa theo bảng tracking
  "": { name: "Trang chủ", id: "home" },
  home: { name: "Trang chủ", id: "home" },
  channels: { name: "Danh sách kênh", id: "live_tv" },
  channel: { name: "Trình phát trực tiếp", id: "player_live" },
  video: { name: "Trình phát video", id: "player_vod" },
  "tim-kiem": { name: "Tìm kiếm", id: "search" },
  result: { name: "Tìm kiếm", id: "search" },
  profile: { name: "Tài khoản", id: "profile" },
  subscription: { name: "Thanh toán", id: "subscription" },
  "subscription-detail": { name: "Thanh toán", id: "subscription" },
  livescore: { name: "Lịch phát sóng", id: "schedule" },
  "detail-livescore": { name: "Lịch phát sóng", id: "schedule" },
  short: { name: "Khám phá", id: "short" },
  "related-shorts": { name: "Khám phá", id: "short" },
  // Tự định nghĩa các trang khác
  league: { name: "Giải đấu", id: "league" },
  category: { name: "Danh mục", id: "category" },
  "danh-muc": { name: "Danh mục", id: "category" },
  "chart-rankings": { name: "Bảng xếp hạng", id: "chart_rankings" },
  "chinh-sach": { name: "Chính sách", id: "policy" },
  "dieu-khoan-giao-dich": { name: "Điều khoản", id: "terms" },
  "cancel-subcription": { name: "Hủy gói", id: "cancel_subscription" },
  android: { name: "Android", id: "android" },
  ios: { name: "iOS", id: "ios" },
  404: { name: "Không tìm thấy", id: "not_found" }
}

/**
 * Helper function để lấy page info với xử lý đặc biệt cho trang video
 * @param {string} path - Đường dẫn trang (bao gồm query string)
 * @returns {{ name: string, id: string }} - Page info object
 */
const getCustomPageInfo = (path) => {
  const [pathname, queryString] = (path || "").split("?")
  const segment = pathname.split("/").filter(Boolean)[0] || "home"

  // Xử lý đặc biệt cho trang video dựa trên type query param
  if (segment === "video" && queryString) {
    const params = new URLSearchParams(queryString)
    const type = params.get("type")

    if (type === "1") {
      // type = 1 → Live
      return { name: "Trình phát trực tiếp", id: "player_live" }
    } else if (type === "2") {
      // type = 2 → VOD
      return { name: "Trình phát video", id: "player_vod" }
    }
    // Default cho video nếu không có type rõ ràng
    return { name: "Trình phát video", id: "player_vod" }
  }

  // Sử dụng PAGE_MAP cho các trang khác
  return (
    PAGE_MAP[segment] || {
      name: segment.charAt(0).toUpperCase() + segment.slice(1),
      id: segment
    }
  )
}

/**
 * Custom hook để khởi tạo và quản lý tracking
 * @param {object} router - Next.js router object
 */
export const useTracking = (router) => {
  // Khởi tạo tracking + page view tracking (chỉ chạy 1 lần)
  useEffect(() => {
    const tracking = getTracking()
    if (!tracking) return

    // Setup tracking utilities trên window
    window.trackingLib = tracking
    window.trackingUtils = {
      getSessionSign,
      getDeviceId,
      getInfo,
      getDeviceBrand,
      trackViewContentDetails: tracking.trackViewContentDetails,
      trackPlaybackStarted: tracking.trackPlaybackStarted,
      trackPlaybackProgress: tracking.trackPlaybackProgress,
      trackPlaybackCompleted: tracking.trackPlaybackCompleted,
      trackPlaybackPaused: tracking.trackPlaybackPaused,
      trackPlaybackResumed: tracking.trackPlaybackResumed,
      trackBufferStarted: tracking.trackBufferStarted,
      trackBufferEnded: tracking.trackBufferEnded,
      trackQualityChanged: tracking.trackQualityChanged,
      trackPlaybackError: tracking.trackPlaybackError
    }

    // Lấy hoặc tạo session_id
    const sessionId = tracking.getOrCreateSessionId({
      expirationMinutes: 30
    })

    // Init tracking
    tracking.init({
      baseURL: process.env.NEXT_PUBLIC_TRACKING_URL,
      appName: "ONPlus",
      appId: "ONPlus",
      packageId: "com.vtvcab.onsportstv",
      platform: "WebPC",
      debug: false, //process.env.NODE_ENV === "development",
      sessionCookieConfig: {
        expirationMinutes: 30
      },
      secretKey: process.env.NEXT_PUBLIC_SECRET_KEY,
      sessionSign: getSessionSign(sessionId, process.env.NEXT_PUBLIC_SECRET_KEY),
      deviceId: getDeviceId(),
      deviceModel: navigator.userAgent,
      userId: getInfo().id,
      deviceBrand: getDeviceBrand(),
      pageMap: PAGE_MAP,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
      buildNumber: process.env.NEXT_PUBLIC_BUILD_NUMBER
    })

    // Init session tracking
    tracking.initSession({
      progressInterval: 5 * 60 * 1000, // 5 phút,
      idleTimeout: 30 * 60 * 1000,
      autoStart: true,
      debug: false, //process.env.NODE_ENV === "development",
      sessionCookieConfig: {
        expirationMinutes: 30
      }
    })

    // Biến lưu referrer page id
    let lastPageId = null

    // Custom track page view function với xử lý đặc biệt cho video
    const trackPageViewCustom = (path) => {
      const pageInfo = getCustomPageInfo(path)
      tracking.sendEvent({
        event_name: "page_view",
        page_name: pageInfo.name,
        page_id: pageInfo.id,
        page_path: path,
        referrer_page_id: lastPageId || ""
      })
      lastPageId = pageInfo.id
    }

    // Track trang hiện tại
    trackPageViewCustom(router.asPath)

    // Listener cho route changes
    const handleRouteChange = (url) => {
      trackPageViewCustom(url)
    }

    router.events.on("routeChangeComplete", handleRouteChange)

    return () => {
      tracking.cleanupSession()
      router.events.off("routeChangeComplete", handleRouteChange)
    }
  }, []) // Chỉ chạy 1 lần khi mount

  // Cập nhật userId khi user thay đổi
  useEffect(() => {
    const tracking = getTracking()
    if (!tracking) return

    tracking.setUserInfo({ userId: getInfo().id })
  }, [getInfo().id])
}

export { getTracking }
export default useTracking
